import React from 'react'

import { ApolloCache, useMutation } from '@apollo/client'

import Alert from '@web/components/Modal/Alert'
import { useModal } from '@web/components/Modal/useModal'
import { openErrorToast } from '@web/components/Toast/GlobalToastPortal'
import { gql } from '@web/gql'
import { UpdateMyBlockCountFragment, UseBlockModal_UserFragment } from '@web/gql/graphql'
import { extractByTypename, isTypename } from '@web/libs/apollo/typeGuard'
import { messages } from '@web/libs/constants'
import nClicks from '@web/libs/nlog/click/codes'
import { extractErrorMessage, neloLogger } from '@web/libs/utils'

interface Props {
  user: UseBlockModal_UserFragment
}

const updateMyBlockCount = (cache: ApolloCache<any>, count = 1) => {
  cache.modify({
    fields: {
      me(me) {
        if (!me?.user) return me

        const userRef = cache.identify(me.user)
        if (!userRef) return me

        const fragment = gql(`
          fragment UpdateMyBlockCount on User {
            stat {
              blockCount
            }
          }
        `)

        const prevUser = cache.readFragment({ fragment, id: userRef }) as UpdateMyBlockCountFragment

        if (!prevUser?.stat.blockCount) return me

        cache.writeFragment({
          id: userRef,
          data: {
            ...prevUser,
            stat: {
              ...prevUser.stat,
              blockCount: prevUser.stat.blockCount + count,
            },
          },
          fragment,
        })

        return me
      },
    },
  })
}

const blockMutation = gql(`
  mutation BlockUser($input: BlockUserInput!) {
    blockUser(input: $input) {
      __typename
      ... on BlockUser_Succeed {
        success
      }
      ... on BaseError {
        message
      }
    }
  }
`)

const unBlockMutation = gql(`
  mutation UnblockUser($input: UnblockUserInput!) {
    unblockUser(input: $input) {
      __typename
      ... on UnblockUser_Succeed {
        success
      }
      ... on BaseError {
        message
      }
    }
  }
`)

const useBlockModal = ({ user }: Props) => {
  const [blockUser] = useMutation(blockMutation, {
    update: (cache, { data }) => {
      if (isTypename(data?.blockUser, 'BlockUser_Succeed')) {
        cache.modify({
          id: cache.identify(user),
          fields: {
            isBlocked: () => true,
          },
        })
        updateMyBlockCount(cache, +1)
      }
    },
    onCompleted: data => {
      const { blockUser } = data

      const { BlockUser_Duplicated, BlockUser_Limit, BlockUser_MySelf, UnknownError } = extractByTypename(blockUser)

      if (BlockUser_Limit) {
        openBlockLimitModal()
        return
      }

      if (BlockUser_Duplicated) {
        openErrorToast(BlockUser_Duplicated.message)
        neloLogger('warn', `blockUser userId: ${user?.id} ${BlockUser_Duplicated.message}`)
        return
      }

      if (BlockUser_MySelf) {
        openErrorToast(BlockUser_MySelf.message)
        neloLogger('warn', `blockUser userId: ${user?.id} ${BlockUser_MySelf.message}`)
        return
      }

      if (UnknownError) {
        openErrorToast(messages.ERR_MUTATION)
        neloLogger('error', `blockUser userId: ${user?.id} ${UnknownError.message}`)
        return
      }
    },
    onError: error => {
      openErrorToast(messages.ERR_COMMON)
      neloLogger('error', `blockUser ${extractErrorMessage(error)}`)
    },
  })

  const [unblockUser] = useMutation(unBlockMutation, {
    update: (cache, { data }) => {
      if (isTypename(data?.unblockUser, 'UnblockUser_Succeed')) {
        cache.modify({
          id: cache.identify(user),
          fields: {
            isBlocked: () => false,
          },
        })
        updateMyBlockCount(cache, -1)
      }
    },
    onCompleted: data => {
      if (isTypename(data?.unblockUser, 'UnblockUser_NotFound')) {
        openErrorToast('차단되지 않은 사용자입니다.')
        neloLogger('warn', `unblockUser userId: ${user?.id} ${data?.unblockUser.message}`)
      }

      if (isTypename(data?.unblockUser, 'UnknownError')) {
        openErrorToast(messages.ERR_MUTATION)
        neloLogger('error', `unblockUser userId: ${user?.id} ${data?.unblockUser.message}`)
      }
    },
    onError: error => {
      openErrorToast(messages.ERR_COMMON)
      neloLogger('error', `unblockUser ${extractErrorMessage(error)}`)
    },
  })

  const { openModal: openBlockModal } = useModal(Alert, {
    label: {
      confirm: '차단',
      cancel: '취소',
    },
    title: user?.profile.nickname,
    message: (
      <>
        차단하시겠어요?
        <br />
        차단하면 피드에서 서로의 리뷰를 확인할 수 없고, 팔로우도 불가능합니다.
      </>
    ),
    callback: {
      confirm: async () => {
        await blockUser({ variables: { input: { userIdno: user.idno } } })
      },
    },
    clickCodes: {
      confirm: nClicks.reviewerBlockPopup.BLOCKOK,
      cancel: nClicks.reviewerBlockPopup.BLOCKNO,
    },
  })

  const { openModal: openUnBlockModal } = useModal(Alert, {
    label: {
      confirm: '해제',
      cancel: '취소',
    },
    title: user?.profile.nickname,
    message: (
      <>
        차단을 해제하시겠어요?
        <br />
        해제 후에는 서로의 리뷰를 확인할 수 있고,
        <br />
        팔로우도 가능합니다.
      </>
    ),
    callback: {
      confirm: async () => {
        await unblockUser({ variables: { input: { userIdno: user.idno } } })
      },
    },
    clickCodes: {
      confirm: nClicks.reviewerBlockPopup.UNBLOCKOK,
      cancel: nClicks.reviewerBlockPopup.UNBLOCKNO,
    },
  })

  const { openModal: openBlockLimitModal } = useModal(Alert, {
    label: {
      confirm: '확인',
    },
    message: (
      <>
        리뷰어 차단은 최대 100명까지
        <br />
        추가할 수 있습니다.
      </>
    ),
    clickCodes: {
      confirm: nClicks.reviewerBlockPopup.LIMITCLOSE,
    },
  })

  return {
    openBlockModal,
    openUnBlockModal,
  }
}

useBlockModal.fragments = {
  user: gql(`
    fragment UseBlockModal_User on User {
      id
      idno
      profile {
        nickname
      }
      isBlocked
    }
  `),
}

export default useBlockModal
